/**
 * A small JPEG of a file the reader just picked — the first frame of a video, a scaled copy of an
 * image — or null.
 *
 * **Drawn HERE because the browser already holds the file.** A frame from a 250 MB video cannot be
 * drawn on a list without downloading all 250 MB of it; drawn now it costs a few kilobytes, once.
 * The server verifies it is a JPEG by its magic bytes and files it beside the file.
 *
 * **It never throws and never blocks the upload.** A PDF has no thumbnail; a video the browser
 * cannot decode (an MP4 carrying HEVC on a browser without it) or one that takes too long gets
 * none; the tile then shows its type's glyph. The object URL is the reader's OWN file in their own
 * tab, and it is revoked before this returns.
 */
export async function drawThumbnail(file: File): Promise<Blob | null> {
  try {
    if (file.type === 'image/png' || file.type === 'image/jpeg') return await fromImage(file);
    if (file.type === 'video/mp4') return await fromVideo(file);
  } catch {
    // Any failure is the absence of a thumbnail, never a failed upload.
  }

  return null;
}

/** The longest edge, in pixels. Tiles render at about half this, so a 2x screen stays sharp. */
const EDGE = 480;

const QUALITY = 0.8;

/** A video that has not produced a frame by now gets no thumbnail. */
const VIDEO_TIMEOUT_MS = 8000;

async function fromImage(file: File): Promise<Blob | null> {
  const bitmap = await createImageBitmap(file);

  try {
    return await encode(bitmap, bitmap.width, bitmap.height);
  } finally {
    bitmap.close();
  }
}

function fromVideo(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    let settled = false;

    const finish = (result: Blob | null) => {
      if (settled) return;
      settled = true;

      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(result);
    };

    const timer = window.setTimeout(() => finish(null), VIDEO_TIMEOUT_MS);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    // A frame a little way in — the very first is often black.
    video.addEventListener('loadedmetadata', () => {
      video.currentTime = Math.min(1, Number.isFinite(video.duration) ? video.duration / 2 : 0);
    });

    video.addEventListener('seeked', () => {
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        finish(null);
        return;
      }

      encode(video, video.videoWidth, video.videoHeight).then(finish, () => finish(null));
    });

    video.addEventListener('error', () => finish(null));

    video.src = url;
  });
}

function encode(source: CanvasImageSource, width: number, height: number): Promise<Blob | null> {
  const scale = Math.min(1, EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');

  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');

  if (!context) return Promise.resolve(null);

  context.drawImage(source, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', QUALITY));
}
